import apiClient from '../../lib/axios';

/**
 * Folder tree. Backend returns the whole tree by default.
 * Optional filters: repository (name), groupCategory, groupSubCategory.
 * @param {Object} [filters]
 * @param {string} [filters.repository] - Repository name (from /api/repositories/active)
 * @param {string} [filters.groupCategory] - 'Confidential' | 'General' | 'Personal'
 * @param {string} [filters.groupSubCategory]
 * @returns {Promise<{ data?: { folders?, childrenFolders?, children? } }>}
 */
export const getFolderTree = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.repository) params.set('repository', filters.repository);
  if (filters.groupCategory) params.set('groupCategory', filters.groupCategory);
  if (filters.groupSubCategory) params.set('groupSubCategory', filters.groupSubCategory);
  const query = params.toString();
  const url = query ? `/api/folders/tree?${query}` : '/api/folders/tree';
  const { data } = await apiClient.get(url);
  return data;
};

/**
 * Contents of one folder: folder + childrenFolders + documents. Documents are paginated.
 * @param {Object} options - folderId (or folderNumber+repository), and optional page, limit for documents
 * @param {string} [options.folderId] - Folder ID
 * @param {string} [options.folderNumber] - Folder number (use with repository)
 * @param {string} [options.folderName] - Folder name (use with repository)
 * @param {string} [options.repository] - Repository name
 * @param {number} [options.page] - Page number for documents (1-based)
 * @param {number} [options.limit] - Page size for documents
 * @returns {Promise<{ data?: { folder?, childrenFolders?, documents?, pagination? } }>}
 */
export const getFolderContents = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.folderId) params.set('folderId', options.folderId);
  if (options.folderNumber) params.set('folderNumber', options.folderNumber);
  if (options.folderName) params.set('folderName', options.folderName);
  if (options.repository) params.set('repository', options.repository);
  if (options.page != null) params.set('page', String(options.page));
  if (options.limit != null) params.set('limit', String(options.limit));
  const { data } = await apiClient.get(`/api/folders/contents?${params.toString()}`);
  return data;
};

/**
 * Single folder by ID (e.g. for selection/details).
 * @param {string} id - Folder ID
 */
export const getFolderById = async (id) => {
  const { data } = await apiClient.get(`/api/folders/${id}`);
  return data;
};

/**
 * Create folder.
 * Payload: { folderName, folderNumber, groupCategory, groupSubCategory, repository, parentFolderId?, folderTypeId? }
 * @param {Object} payload
 */
export const createFolder = async (payload) => {
  const { data } = await apiClient.post('/api/folders', payload);
  return data;
};

/**
 * Create folder and file temporary documents into it.
 * POST /api/folders/create-with-documents
 *
 * Payload: { folderName, folderNumber, groupCategory, groupSubCategory, repository, parentFolderId?, folderTypeId?, documentIds?, documentTypeId?, title? }
 * @param {Object} payload
 */
export const createFolderWithDocuments = async (payload) => {
  const { data } = await apiClient.post('/api/folders/create-with-documents', payload);
  return data;
};

/**
 * Update folder (name, number, type, etc.).
 * @param {string} id - Folder ID
 * @param {Object} payload
 */
export const updateFolder = async (id, payload) => {
  const { data } = await apiClient.patch(`/api/folders/${id}`, payload);
  return data;
};

/**
 * Delete folder (fails if it has children or documents).
 * @param {string} id - Folder ID
 */
export const deleteFolder = async (id) => {
  const { data } = await apiClient.delete(`/api/folders/${id}`);
  return data;
};

/**
 * Get non-root folders by folder type.
 * Returns all active, non-deleted folders that have the given folder type and are not root folders (have a parent).
 * No pagination.
 *
 * @param {{ folderTypeId: string, repository?: string }} params
 */
export const getNonRootFoldersByType = async ({ folderTypeId, repository } = {}) => {
  if (!folderTypeId) {
    throw new Error('folderTypeId is required for getNonRootFoldersByType');
  }
  const qs = new URLSearchParams();
  qs.set('folderTypeId', String(folderTypeId));
  if (repository) qs.set('repository', String(repository));
  const { data } = await apiClient.get(`/api/folders/non-root-by-type?${qs.toString()}`);
  return data;
};

/**
 * Get root folders under NEW_ARCHIVE.
 * GET /api/folders/roots
 *
 * @param {{ groupCategory?: 'Confidential'|'General'|'Personal' }} [params]
 */
export const getRootFolders = async (params = {}) => {
  const qs = new URLSearchParams();
  if (params.groupCategory) qs.set('groupCategory', String(params.groupCategory));
  const url = qs.toString() ? `/api/folders/roots?${qs.toString()}` : '/api/folders/roots';
  const { data } = await apiClient.get(url);
  return data;
};

/**
 * Get Personal subroot folders (Staff, Members) under NEW_ARCHIVE.
 * GET /api/folders/roots/personal-subroots
 */
export const getPersonalSubrootFolders = async () => {
  const { data } = await apiClient.get('/api/folders/roots/personal-subroots');
  return data;
};

/**
 * Get folders by group category (and optional subcategory) and folder type.
 * GET /api/folders/by-group-and-type
 *
 * @param {{
 *   groupCategory: 'Confidential'|'General'|'Personal',
 *   groupSubCategory?: 'Staff'|'Members',
 *   folderTypeId?: string
 * }} params
 */
export const getFoldersByGroupAndType = async (params = {}) => {
  if (!params.groupCategory) {
    throw new Error('groupCategory is required for getFoldersByGroupAndType');
  }
  const qs = new URLSearchParams();
  qs.set('groupCategory', String(params.groupCategory));
  if (params.groupSubCategory) qs.set('groupSubCategory', String(params.groupSubCategory));
  if (params.folderTypeId) qs.set('folderTypeId', String(params.folderTypeId));
  const { data } = await apiClient.get(`/api/folders/by-group-and-type?${qs.toString()}`);
  return data;
};

function getTreeListFromResponse(data) {
  if (!data) return [];
  const raw = data?.data ?? data;
  if (Array.isArray(raw?.folders)) return raw.folders;
  if (Array.isArray(raw?.childrenFolders)) return raw.childrenFolders;
  if (Array.isArray(raw?.children)) return raw.children;
  if (Array.isArray(raw)) return raw;
  return [];
}

function getChildFolders(node) {
  const c = node?.children ?? node?.subfolders ?? node?.childrenFolders ?? node?.childFolders;
  return Array.isArray(c) ? c : [];
}

/**
 * Flatten folder tree response into a single array of folder nodes (for dropdowns).
 * @param {Object} data - Response from getFolderTree
 * @returns {Array<{ _id?, id?, folderName?, name?, ... }>}
 */
export function flattenFolderTree(data) {
  const list = getTreeListFromResponse(data);
  const out = [];
  function visit(nodes) {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (node && (node._id ?? node.id)) out.push(node);
      visit(getChildFolders(node));
    }
  }
  visit(list);
  return out;
}

function getNodeName(node) {
  return (node?.name ?? node?.folderName ?? node?.label ?? node?.groupSubCategory ?? '').toString().trim();
}

function nodeMatchesSubCategory(node, subCategoryNorm) {
  if (!node || !subCategoryNorm) return false;
  const name = getNodeName(node).toLowerCase();
  if (name === subCategoryNorm) return true;
  const prop = (node?.groupSubCategory ?? node?.subCategory ?? '').toString().trim().toLowerCase();
  return prop === subCategoryNorm;
}

/**
 * Get the root folder for a category in a repository (e.g. the "Confidential" or "General" root).
 * For Personal with groupSubCategory (Staff/Member), returns the Staff or Member folder so creation is under /Personal/Staff/ or /Personal/Member/.
 * @param {string} repository - Repository name
 * @param {string} groupCategory - 'Confidential' | 'General' | 'Personal'
 * @param {string} [groupSubCategory] - Optional for Personal (Staff, Member)
 * @returns {Promise<{ _id: string } | null>} The root folder or null
 */
export const getCategoryRootFolder = async (repository, groupCategory, groupSubCategory) => {
  if (!repository || !groupCategory) return null;

  const data = await getFolderTree({ repository, groupCategory });
  const list = getTreeListFromResponse(data);
  const root = list[0] ?? null;
  if (!root || !(root._id ?? root.id)) return null;

  if (groupCategory === 'Personal' && groupSubCategory) {
    const subCategoryNorm = String(groupSubCategory).trim().toLowerCase();
    const personalNorm = 'personal';
    let personalNode = root;
    const rootName = getNodeName(root).toLowerCase();
    if (rootName !== personalNorm) {
      const rootChildren = getChildFolders(root);
      personalNode = rootChildren.find((c) => getNodeName(c).toLowerCase() === personalNorm) ?? null;
    }
    if (personalNode) {
      const children = getChildFolders(personalNode);
      let sub = children.find((c) => nodeMatchesSubCategory(c, subCategoryNorm));
      if (!sub && list.length > 1) {
        sub = list.find((c) => nodeMatchesSubCategory(c, subCategoryNorm));
      }
      if (sub && (sub._id ?? sub.id)) return { _id: sub._id ?? sub.id };
    }
  }

  return { _id: root._id ?? root.id };
};

/**
 * Ensure the category root (Confidential, General, or Personal) exists under the repository;
 * for Personal with groupSubCategory, ensure the subgroup (Staff/Member) exists under Personal.
 * Creates missing folders as needed. Returns the folder id to use as parent for the new folder.
 * @param {string} repository - Repository name (e.g. NEW_ARCHIVE, OLD_ARCHIVE)
 * @param {string} groupCategory - 'Confidential' | 'General' | 'Personal'
 * @param {string} [groupSubCategory] - For Personal: 'Staff' | 'Member'
 * @param {string} repositoryFolderId - The repository node's folder id (parent when creating roots)
 * @returns {Promise<string>} Parent folder id to use for the new subfolder
 */
export const ensureCategoryRootAndGetParentId = async (
  repository,
  groupCategory,
  groupSubCategory,
  repositoryFolderId
) => {
  if (!repository || !groupCategory || !repositoryFolderId) return repositoryFolderId;

  const existing = await getCategoryRootFolder(repository, groupCategory, groupSubCategory || undefined);
  if (existing?._id) return existing._id;

  if (groupCategory === 'Personal' && groupSubCategory) {
    const subCategoryTrimmed = String(groupSubCategory).trim();
    const personalRoot = await getCategoryRootFolder(repository, 'Personal');
    let personalId = personalRoot?._id;
    if (!personalId) {
      const createdPersonal = await createFolder({
        folderName: 'Personal',
        folderNumber: '',
        repository,
        groupCategory: 'Personal',
        parentFolderId: repositoryFolderId,
      });
      personalId = createdPersonal?._id ?? createdPersonal?.id;
      if (!personalId) throw new Error('Failed to create Personal root folder');
    }
    const createdSub = await createFolder({
      folderName: subCategoryTrimmed,
      folderNumber: '',
      repository,
      groupCategory: 'Personal',
      groupSubCategory: subCategoryTrimmed,
      parentFolderId: personalId,
    });
    const subId = createdSub?._id ?? createdSub?.id;
    if (!subId) throw new Error('Failed to create Personal subcategory folder');
    return subId;
  }

  const created = await createFolder({
    folderName: groupCategory,
    folderNumber: '',
    repository,
    groupCategory,
    ...(groupCategory === 'Personal' && { groupSubCategory: (groupSubCategory || '').trim() || undefined }),
    parentFolderId: repositoryFolderId,
  });
  const id = created?._id ?? created?.id;
  if (!id) throw new Error('Failed to create category root folder');
  return id;
};

/**
 * Search folders (e.g. global search in tree). Supports pagination.
 * @param {Object} params - q, repository, groupCategory, folderTypeId, folderName, folderNumber, page, limit
 * @returns {Promise<{ data?: array, folders?: array, pagination?: { page, limit, total, pages } }>}
 */
export const searchFolders = async (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.q) searchParams.set('q', params.q);
  if (params.repository) searchParams.set('repository', params.repository);
  if (params.groupCategory) searchParams.set('groupCategory', params.groupCategory);
  if (params.folderTypeId) searchParams.set('folderTypeId', params.folderTypeId);
  if (params.folderName) searchParams.set('folderName', params.folderName);
  if (params.folderNumber) searchParams.set('folderNumber', params.folderNumber);
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.limit != null) searchParams.set('limit', String(params.limit));
  const { data } = await apiClient.get(`/api/folders/search?${searchParams.toString()}`);
  return data;
};

/** @deprecated Use getFolderTree({ repository, groupCategory, groupSubCategory }). */
export const fetchFolderTree = async (repositoryName, groupCategory, groupSubCategory) =>
  getFolderTree({ repository: repositoryName || undefined, groupCategory, groupSubCategory });

/** @deprecated Use getFolderContents({ folderId }). */
export const fetchFolderContents = async (folderId) =>
  getFolderContents({ folderId });

export default getFolderTree;